import React, { useEffect, useState } from "react";
import { Fingerprint, ScanFace, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const isApple = () => /iPhone|iPad|Macintosh/i.test(navigator.userAgent);
export const biometricLabel = () => (isApple() ? "Face ID / Touch ID" : /Android/i.test(navigator.userAgent) ? "Fingerprint / Face" : /Windows/i.test(navigator.userAgent) ? "Windows Hello" : "Fingerprint / Face");

export function useBiometricSupport() {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    const pk = window.PublicKeyCredential;
    if (!pk || !pk.isUserVerifyingPlatformAuthenticatorAvailable) return setOk(false);
    pk.isUserVerifyingPlatformAuthenticatorAvailable().then(setOk).catch(() => setOk(false));
  }, []);
  return ok;
}

export async function enrolBiometric(deviceName) {
  const { startRegistration } = await import("@simplewebauthn/browser");
  const { data: opts } = await api.post("/webauthn/register/options");
  const attResp = await startRegistration({ optionsJSON: { ...opts, authenticatorSelection: { ...(opts.authenticatorSelection || {}), authenticatorAttachment: "platform", userVerification: "required", residentKey: "preferred" } } });
  await api.post("/webauthn/register/verify", { ...attResp, device_name: deviceName || defaultDeviceName() });
}

export const defaultDeviceName = () => {
  const ua = navigator.userAgent;
  return /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Macintosh/.test(ua) ? "Mac" : /Android/.test(ua) ? "Android phone" : /Windows/.test(ua) ? "Windows PC" : "This device";
};

export function BiometricIcon({ size = 18 }) {
  return isApple() ? <ScanFace size={size} /> : <Fingerprint size={size} />;
}

/* Big primary sign-in button for the Login page. */
export function BiometricLoginButton({ onClick, busy }) {
  const supported = useBiometricSupport();
  return (
    <button type="button" onClick={onClick} disabled={busy} className="w-full mt-5 flex flex-col items-center justify-center gap-0.5 rounded-xl min-h-[48px] px-4 py-2.5 font-semibold bg-[#E11B22] text-white hover:bg-[#c4151b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink transition-colors disabled:opacity-60 text-center" data-testid="biometric-login-btn" aria-label={`Sign in with ${biometricLabel()} passkey`}>
      <span className="flex items-center justify-center gap-2 text-sm sm:text-base leading-tight">{busy ? <Loader2 size={18} className="animate-spin shrink-0" /> : <BiometricIcon />} <span className="truncate max-w-[75vw]">Sign in with {biometricLabel()}</span></span>
      {supported === false && <span className="text-[11px] font-normal opacity-90 leading-tight">or use a security key</span>}
    </button>
  );
}

/* One-time prompt after a password / code / Google sign-in: turn on biometric sign-in for next time. */
export function BiometricNudge() {
  const { user } = useAuth();
  const supported = useBiometricSupport();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const key = user ? `sn_bio_nudge_${user.id || user.email}` : null;
  useEffect(() => {
    if (!user || !supported || !key || localStorage.getItem(key)) return setShow(false);
    api.get("/webauthn/credentials").then(({ data }) => setShow((data || []).length === 0)).catch(() => setShow(false));
  }, [user?.id, supported]);
  if (!show) return null;
  const dismiss = () => { localStorage.setItem(key, "1"); setShow(false); };
  const enrol = async () => {
    setBusy(true);
    try { await enrolBiometric(); toast.success(`${biometricLabel()} sign-in is on for this device`); dismiss(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not set up — you can try again from Account → Sign-in methods"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px] z-[60] rounded-2xl border border-border bg-card shadow-xl p-4 animate-in slide-in-from-bottom-4" data-testid="biometric-nudge">
      <button onClick={dismiss} className="absolute right-3 top-3 text-muted-foreground" aria-label="Not now" data-testid="biometric-nudge-close"><X size={16} /></button>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald/10 text-emerald grid place-items-center shrink-0"><BiometricIcon size={22} /></div>
        <div className="min-w-0">
          <div className="font-display font-bold">Sign in faster next time</div>
          <p className="text-xs text-muted-foreground mt-0.5">Use {biometricLabel()} on this device instead of typing your password. Your biometrics never leave your device — SHOP NOW only stores a secure key.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={enrol} disabled={busy} className="rounded-xl bg-ink text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="biometric-nudge-enrol">{busy ? <Loader2 size={14} className="animate-spin" /> : <BiometricIcon size={14} />} Turn on</button>
            <button onClick={dismiss} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold" data-testid="biometric-nudge-later">Not now</button>
          </div>
        </div>
      </div>
    </div>
  );
}
